import { Injectable, inject, EnvironmentInjector, runInInjectionContext } from '@angular/core';
import { Firestore, doc, getDoc, updateDoc, onSnapshot, collection, query, where, getCountFromServer } from '@angular/fire/firestore';
import { Functions } from '@angular/fire/functions';
import { httpsCallable } from './telemetry';
import { Observable, from, map, switchMap, timer } from 'rxjs';
import { environment } from '../../../environments/environment';
import { StoredBusinessVerification } from '../utils/business-verification.utils';

/** What Meta told us about a signup that did not produce a code. */
export interface SignupErrorDetail {
  /** The screen the customer was on. */
  step: string | null;
  /** Meta's session id — the first thing their support asks for. */
  sessionId: string | null;
  /** Meta's own message, when it gave one. */
  message: string | null;
}

/**
 * A signup that ended without a code.
 *
 * Carries the detail rather than flattening it into a string, so the caller
 * can tell a cancellation from a failure, show the session id, and record
 * where the funnel leaks. Everything used to arrive as a bare
 * "Connection Cancelled", which was wrong half the time and unactionable the
 * rest.
 */
export class SignupFailedError extends Error {
  constructor(
    message: string,
    readonly detail: SignupErrorDetail,
    /** True when the customer closed the dialog rather than hitting an error. */
    readonly cancelled = false
  ) {
    super(message);
    this.name = 'SignupFailedError';
  }
}

export interface WhatsAppPhoneNumber {
  id: string;
  displayPhoneNumber: string;
  verifiedName: string;
  qualityRating: string;
  status: string;
  isConnected: boolean;
  isPending: boolean;
  messagingLimitTier?: string;
  messagingLimitValue?: number;
  limitsUpdatedAt?: any;
  wabaId?: string;
  nameChangeStatus?: 'approved' | 'rejected' | 'pending_review' | 'expired';
  pendingVerifiedName?: string;
  nameChangeRejectionReason?: string;
  certificateStatus?: 'VERIFIED' | 'NOT_VERIFIED' | 'REVOKED' | 'EXPIRED' | 'RATE_LIMITED' | 'PENDING';
  /**
   * True when this number is shared with the customer's WhatsApp Business app
   * (Coexistence). Such a number arrived already registered, so it owes Meta
   * neither an ownership code nor a two-step PIN — see phoneSetupStep.
   */
  isOnBizApp?: boolean;
  /** Meta's platform_type for the number, e.g. CLOUD_API. */
  platformType?: string | null;
  /** Progress of the one-shot history and contact import. */
  coexistence?: {
    enabled: boolean;
    onboardedAt?: number;
    syncDeadlineAt?: number;
    syncStatus?: 'pending' | 'requested' | 'receiving' | 'complete' | 'failed' | 'expired';
    lastHistoryPhase?: string;
    state?: 'active' | 'offboarded';
  };
  /**
   * Informational only — do NOT use this to decide whether a number can send.
   *
   * It records whether the ownership OTP captured during registration is still on
   * file. Meta lets it lapse to NOT_VERIFIED/EXPIRED on numbers that are connected
   * and sending normally, so gating on it silently hid healthy numbers from every
   * picker in the app. Sendability comes from `status` via
   * `core/utils/whatsapp-phone.utils.ts`.
   */
  codeVerificationStatus?: 'VERIFIED' | 'NOT_VERIFIED' | 'EXPIRED';
}

export interface WhatsAppBusinessProfile {
  about?: string;
  address?: string;
  description?: string;
  email?: string;
  profile_picture_url?: string;
  websites?: string[];
  vertical?: string;
}

export interface WhatsAppMessagingLimit {
  phoneNumberId: string;
  displayPhoneNumber: string;
  verifiedName?: string;
  limitTier: string;
  limitValue: number;
  messagesSent: number;
  messagesRemaining: number;
  error?: string;
}

export interface AffectedAutomation {
  id: string;
  name: string;
  enabled: boolean;
  triggerType?: string;
  templateName?: string;
  phoneNumberDisplay?: string;
}

export interface AffectedSchedule {
  id: string;
  name: string;
  enabled: boolean;
  frequency?: string;
  templateName?: string;
  phoneNumberDisplay?: string;
  nextRunAt?: string | null;
}

export interface AffectedScheduledMessage {
  id: string;
  templateName?: string;
  recipientCount: number;
  recurrence?: string;
  scheduledAt?: string | null;
  fromPhoneNumber?: string;
}

export interface AffectedConversation {
  id: string;
  phoneNumber: string;
  displayName?: string;
  assignedToName?: string;
  unreadCount: number;
}

/**
 * What stops working when a WhatsApp number, or the whole account, is removed.
 *
 * Automations and schedules pin both a number and a template belonging to it, so
 * they cannot survive the number going away — they are deleted, not disabled.
 * Pending campaigns are cancelled and stay visible in the campaign list, and open
 * conversations are archived because nothing can be sent from them any more.
 */
export interface WhatsAppRemovalImpact {
  phoneNumberIds: string[];
  automations: AffectedAutomation[];
  schedules: AffectedSchedule[];
  scheduledMessages: AffectedScheduledMessage[];
  /** Open conversations that will be archived; capped, see conversationsTotal. */
  conversations: AffectedConversation[];
  conversationsTotal: number;
  matchedEverything: boolean;
}

export interface WhatsAppAccount {
  wabaIds: string[];
  phoneNumbers: WhatsAppPhoneNumber[];
  connectedAt: any;
  connectedBy: string;
  status: 'connected' | 'disconnected' | 'pending';
  businessId?: string;
  billingCurrency?: string;
  /**
   * Verification status of the Meta Business Account behind the WABA, as last
   * reported by Meta. Absent until Echo has managed to read one — see
   * `businessVerificationView` in core/utils/business-verification.utils.ts.
   */
  businessVerification?: StoredBusinessVerification;
}

/**
 * Prefill for the Embedded Signup screens.
 *
 * These key names are Meta's, not ours, and they are unforgiving — a
 * misspelled key is silently ignored rather than rejected, so the screen just
 * shows up blank. `zipPostal` (not zipCode), `streetAddress1` (not line1), and
 * `country` must be an ISO 3166-1 alpha-2 code, not a dialling code.
 *
 * @see https://developers.facebook.com/documentation/business-messaging/whatsapp/embedded-signup/pre-filled-data
 */
export interface WhatsAppSignupPrefill {
    business?: {
        name?: string;
        email?: string;
        website?: string;
        address?: {
            streetAddress1?: string;
            streetAddress2?: string;
            city?: string;
            state?: string;
            zipPostal?: string;
            /** ISO 3166-1 alpha-2, e.g. "ZA". */
            country?: string;
        };
        phone?: { code?: number; number?: string };
        timezone?: string;
    };
    phone?: {
        displayName?: string;
        category?: string;
        description?: string;
    };
}


/**
 * Build the Embedded Signup prefill from an organisation document.
 *
 * One builder, because there used to be two — the onboarding screen and the
 * settings screen assembled different payloads from the same document, and the
 * onboarding one read `address.line1` where the data is `street1`, emitted
 * `zipCode` where Meta expects `zipPostal`, and passed a dialling code where an
 * ISO country code belongs. Meta ignores keys it does not recognise, so those
 * fields simply arrived blank with nothing to say why.
 *
 * `null` is stripped: an absent key prefills nothing, which is what we want,
 * whereas an explicit null is just noise in the payload.
 *
 * @param orgData The organisation document, or null before it loads.
 * @param fallbackName Organisation name to use when the document has none.
 * @param userEmail Signed-in user's email, preferred over the org contact.
 * @param countryIso ISO 3166-1 alpha-2 code for the org's country.
 */
export function buildWhatsAppSignupPrefill(
    orgData: any,
    fallbackName: string | null,
    userEmail: string | null,
    countryIso: string | null
): WhatsAppSignupPrefill {
    const name = orgData?.name || fallbackName || undefined;

    const prefill: WhatsAppSignupPrefill = {
        business: {
            name,
            email: userEmail || orgData?.contactEmail || undefined,
            website: orgData?.contactWebsite || undefined,
            timezone: orgData?.timezone || undefined,
            address: {
                streetAddress1: orgData?.address?.street1 || undefined,
                streetAddress2: orgData?.address?.street2 || undefined,
                city: orgData?.address?.city || undefined,
                state: orgData?.address?.state || undefined,
                zipPostal: orgData?.address?.zip || undefined,
                country: countryIso || undefined
            }
        },
        phone: {
            displayName: name,
            category: orgData?.category || 'CHARITY',
            description: orgData?.description || undefined
        }
    };

    if (orgData?.contactPhone) {
        prefill.business!.phone = {
            code: Number(orgData.countryCode) || undefined,
            number: orgData.contactPhone
        };
    }

    // Drop empty branches so Meta receives only what we actually know.
    const prune = (obj: any): any => {
        for (const key of Object.keys(obj)) {
            const value = obj[key];
            if (value && typeof value === 'object') {
                prune(value);
                if (Object.keys(value).length === 0) delete obj[key];
            } else if (value === undefined || value === null || value === '') {
                delete obj[key];
            }
        }
        return obj;
    };

    return prune(prefill);
}

@Injectable({
  providedIn: 'root'
})
export class WhatsAppAccountService {
  /** Resolves once Meta's SDK has loaded and FB.init has run. */
  private sdkReady?: Promise<void>;

  private firestore = inject(Firestore);
  private injector = inject(EnvironmentInjector);
  private functions = inject(Functions);
  private readonly exchangeSignupCodeFn = httpsCallable(this.functions, 'wabExchangeWhatsAppToken');
  private readonly disconnectWhatsAppFn = httpsCallable(this.functions, 'wabDisconnectWhatsAppAccount');
  private readonly getRemovalImpactFn = httpsCallable(this.functions, 'wabGetWhatsAppRemovalImpact');
  private readonly refreshWhatsAppFn = httpsCallable(this.functions, 'wabRefreshWhatsAppAccount');
  private readonly getMessagingLimitsFn = httpsCallable(this.functions, 'wabGetWhatsAppMessagingLimits');
  private readonly checkPaymentMethodFn = httpsCallable(this.functions, 'wabCheckPaymentMethod');
  private readonly getBusinessProfileFn = httpsCallable(this.functions, 'wabGetBusinessProfile');
  private readonly updateBusinessProfileFn = httpsCallable(this.functions, 'wabUpdateBusinessProfile');
  private readonly updateProfilePictureFn = httpsCallable(this.functions, 'wabUpdateProfilePicture');
  private readonly syncTemplatesFn = httpsCallable(this.functions, 'wabSyncWhatsAppTemplates');
  private readonly recordAbandonmentFn = httpsCallable(this.functions, 'wabRecordSignupAbandonment');

  /**
   * Record a signup that ended without a code.
   *
   * Meta names the screen the customer stopped on and stamps every attempt
   * with a session id, which is the first thing their support asks for. Both
   * used to reach the console and nowhere else, so "it didn't work" left
   * nothing to look at and the funnel's drop-off was invisible.
   *
   * Deliberately swallows its own failure: this runs when something has
   * already gone wrong, and a telemetry error must not become the error the
   * customer sees.
   *
   * @param organizationId The organisation the attempt belongs to.
   * @param error What the signup reported.
   * @param coexistence Whether the WhatsApp Business app branch was requested.
   */
  recordSignupAbandonment(
    organizationId: string,
    error: SignupFailedError,
    coexistence = false
  ): void {
    this.recordAbandonmentFn({
      organizationId,
      reason: error.cancelled ? 'cancelled' : 'error',
      step: error.detail?.step ?? null,
      sessionId: error.detail?.sessionId ?? null,
      message: error.message ?? null,
      coexistence
    }).catch(() => {
      // Nothing useful to do, and nothing worth showing.
    });
  }

  /**
   * Exchange the Embedded Signup code for a business access token.
   *
   * No `redirectUri`: v4 goes through FB.login, so there is no redirect and
   * Meta expects none. The code expires 30 seconds after issue.
   */
  exchangeSignupCode(
    code: string,
    organizationId: string,
    wabaId?: string | null,
    session?: { phoneNumberId?: string | null; coexistence?: boolean }
  ): Observable<any> {
    return from(this.exchangeSignupCodeFn({
      code,
      organizationId,
      ...(wabaId ? { wabaId } : {}),
      // Both come from the dialog's session info. Forwarding the number saves
      // the backend asking Meta about every other number on the account, and
      // `coexistence` is what tells it to look at all — the WhatsApp Business
      // app branch is otherwise indistinguishable from an ordinary link.
      ...(session?.phoneNumberId ? { phoneNumberId: session.phoneNumberId } : {}),
      ...(session?.coexistence ? { coexistence: true } : {})
    })).pipe(
      map((result: any) => result.data)
    );
  }

  /**
   * Get WhatsApp account details for an organization
   * @param organizationId The organization ID
   */
  getWhatsAppAccount(organizationId: string): Observable<WhatsAppAccount | null> {
    const orgRef = doc(this.firestore, `organizations/${organizationId}`);
    return from(runInInjectionContext(this.injector, () => getDoc(orgRef))).pipe(
      map(docSnap => {
        if (docSnap.exists()) {
          const data = docSnap.data();
          return data['whatsapp'] || null;
        }
        return null;
      })
    );
  }

  /**
   * Get real-time WhatsApp account details for an organization
   * This returns an Observable that emits whenever the WhatsApp data changes
   * @param organizationId The organization ID
   */
  getWhatsAppAccountRealtime(organizationId: string): Observable<WhatsAppAccount | null> {
    return new Observable(observer => {
      const orgRef = doc(this.firestore, 'organizations', organizationId);

      const unsubscribe = runInInjectionContext(this.injector, () => onSnapshot(orgRef,
        (docSnap) => {
          if (docSnap.exists()) {
            const data = docSnap.data();
            if (data && data['whatsapp']) {
              observer.next(data['whatsapp'] as WhatsAppAccount);
            } else {
              observer.next(null);
            }
          } else {
            observer.next(null);
          }
        },
        (error) => {
          observer.error(error);
        }
      ));

      return () => unsubscribe();
    });
  }

  /**
   * List the automations, schedules and pending campaigns that removing the
   * given numbers would take down.
   *
   * Called before disconnecting so the confirmation dialog can name them.
   * @param organizationId The organization ID
   * @param phoneNumberIds Numbers being removed; omit for the whole account
   */
  getRemovalImpact(organizationId: string, phoneNumberIds?: string[]): Observable<WhatsAppRemovalImpact> {
    return from(this.getRemovalImpactFn({
      organizationId,
      ...(phoneNumberIds?.length ? { phoneNumberIds } : {})
    })).pipe(
      map((result: any) => result.data as WhatsAppRemovalImpact)
    );
  }

  /**
   * Disconnect WhatsApp account from organization
   * Calls Cloud Function to unsubscribe from webhooks and mark as disconnected
   * @param organizationId The organization ID
   */
  disconnectWhatsAppAccount(organizationId: string): Observable<any> {
    return from(this.disconnectWhatsAppFn({ organizationId })).pipe(
      map((result: any) => result.data)
    );
  }

  /**
   * Refresh WhatsApp account data from Meta API
   * Updates phone numbers and verification status
   * @param organizationId The organization ID
   */
  refreshWhatsAppAccount(organizationId: string): Observable<any> {
    return from(this.refreshWhatsAppFn({ organizationId })).pipe(
      map((result: any) => result.data)
    );
  }

  /**
   * Get WhatsApp messaging limits for all phone numbers
   * @param organizationId The organization ID
   */
  getMessagingLimits(organizationId: string): Observable<WhatsAppMessagingLimit[]> {
    return from(this.getMessagingLimitsFn({ organizationId })).pipe(
      map((result: any) => result.data.limits)
    );
  }

  /**
   * Check WhatsApp Business Account payment method status
   * @param organizationId The organization ID
   */
  checkPaymentMethod(organizationId: string): Observable<any> {
    return from(this.checkPaymentMethodFn({ organizationId })).pipe(
      map((result: any) => result.data)
    );
  }

  /**
   * Messaging-tier usage: unique people sent a template outside a customer
   * service window in the last 24 hours, including campaign recipients still
   * queued. Meta meters this per business portfolio, shared by all its numbers.
   *
   * Reads organizations/{orgId}/tierUsage/{scopeId}/tierRecipients with a
   * server-side count, refreshed every minute. A live listener would stream
   * every recipient document to the topbar on every page.
   * @param organizationId The organization ID
   * @param scopeId        The portfolio (`businessId`), or the phone number ID
   *                       when the portfolio is not known
   */
  getTierUsage(organizationId: string, scopeId: string): Observable<number> {
    return timer(0, WhatsAppAccountService.TIER_USAGE_REFRESH_MS).pipe(
      switchMap(() => runInInjectionContext(this.injector, () => {
        const recipients = collection(
          this.firestore, 'organizations', organizationId, 'tierUsage', scopeId, 'tierRecipients'
        );
        const cutoff = Date.now() - 24 * 60 * 60 * 1000;
        return from(getCountFromServer(query(recipients, where('countedAt', '>', cutoff))));
      })),
      map(snapshot => snapshot.data().count)
    );
  }

  /** How often the tier-usage count is refreshed. */
  private static readonly TIER_USAGE_REFRESH_MS = 60 * 1000;

  /**
   * How long to wait for the signup popup before giving up on it.
   *
   * Generous, because the flow has several screens and a business verification
   * step. It exists only so a blocked pop-up surfaces as an explanation rather
   * than a spinner that never stops.
   */
  private static readonly SIGNUP_TIMEOUT_MS = 10 * 60 * 1000;

  /**
   * Every session event that means the customer finished.
   *
   * Only `FINISH` used to count, so the other four were reported back as
   * "cancelled before it completed" — including
   * `FINISH_WHATSAPP_BUSINESS_APP_ONBOARDING`, which is the one Coexistence
   * always ends on. A customer who had just finished was told they had not.
   *
   * - `FINISH` — a number onboarded the ordinary way.
   * - `FINISH_WHATSAPP_BUSINESS_APP_ONBOARDING` — Coexistence.
   * - `FINISH_ONLY_WABA` — the WABA was shared, but no number was added yet.
   * - `FINISH_OBO_MIGRATION` — a number moved off an on-behalf-of account.
   * - `FINISH_GRANT_ONLY_API_ACCESS` — API access granted on existing assets.
   */
  private static readonly FINISH_EVENTS = new Set([
    'FINISH',
    'FINISH_WHATSAPP_BUSINESS_APP_ONBOARDING',
    'FINISH_ONLY_WABA',
    'FINISH_OBO_MIGRATION',
    'FINISH_GRANT_ONLY_API_ACCESS'
  ]);

  /**
   * Load Meta's JavaScript SDK once, and initialise it.
   *
   * Embedded Signup v4 runs through Facebook Login for Business, which is only
   * available through this SDK — the v2/v3 approach of hand-building a
   * `dialog/oauth` URL and opening a popup is not a v4 flow no matter what the
   * `extras` say.
   *
   * Injected on demand rather than in index.html so the SDK never loads for the
   * majority of users, who never open Organisation Settings. Same pattern the
   * app already uses for the Echo support chat widget.
   */
  /**
   * Start loading Meta's SDK ahead of time.
   *
   * Call this when a page that can open signup is shown. `FB.login` opens a
   * pop-up, and a browser only allows that from inside a user gesture —
   * awaiting a script injection first loses the gesture and the pop-up is
   * blocked, which Safari does reliably. Warming the SDK in advance is what
   * lets the click handler reach `FB.login` synchronously.
   *
   * Safe to call repeatedly; the load happens once.
   */
  preloadFacebookSdk(): void {
    this.loadFacebookSdk().catch(() => {
      // Nothing to do here. The click path surfaces the failure properly; a
      // rejected warm-up must not raise an unhandled rejection.
    });
  }

  /** True once `FB.login` can be called without awaiting anything. */
  get isSdkReady(): boolean {
    return Boolean((window as any).FB);
  }

  private loadFacebookSdk(): Promise<void> {
    if (this.sdkReady) return this.sdkReady;

    this.sdkReady = new Promise<void>((resolve, reject) => {
      if ((window as any).FB) {
        resolve();
        return;
      }

      (window as any).fbAsyncInit = () => {
        (window as any).FB.init({
          appId: environment.whatsapp.metaAppId,
          // Must match the Graph version the backend calls; see
          // META_GRAPH_API_URL in functions/src/config.
          version: 'v25.0',
          xfbml: true,
          autoLogAppEvents: true
        });
        resolve();
      };

      const script = document.createElement('script');
      script.src = 'https://connect.facebook.net/en_US/sdk.js';
      script.async = true;
      script.defer = true;
      script.crossOrigin = 'anonymous';
      script.onerror = () => reject(new Error('Could not load the Facebook SDK. Check for an ad blocker or network restriction.'));
      document.body.appendChild(script);
    });

    return this.sdkReady;
  }

  /**
   * Run WhatsApp Embedded Signup (v4) and return the exchangeable code.
   *
   * v4 takes no `sessionInfoVersion` or `version` — products are selected on
   * the Facebook Login for Business configuration instead, and choosing them
   * is what makes a configuration v4.
   *
   * `feature_type` is the one exception, and it survived into v4 precisely
   * because it selects a *flow* rather than a product: passing
   * `whatsapp_business_app_onboarding` offers the customer the branch that
   * adopts the number already live in their WhatsApp Business app, instead of
   * onboarding a fresh one. Meta's v4 notes say Business-app onboarding
   * "continues to be supported through the feature_type parameter".
   *
   * Two channels come back and both matter: `FB.login`'s callback carries the
   * OAuth code, while a `message` event from facebook.com carries the session
   * info — `phone_number_id`, `waba_id` and `business_id` on success, or
   * `current_step` on abandonment. The code has a **30 second TTL**, so it is
   * exchanged server-side immediately.
   *
   * @param setupData Optional prefill for the signup screens
   */
  openEmbeddedSignup(
    setupData?: WhatsAppSignupPrefill,
    options?: { coexistence?: boolean }
  ): Promise<{
    code: string;
    wabaId: string | null;
    phoneNumberId: string | null;
    businessId: string | null;
    coexistence: boolean;
  }> {
    // Deliberately NOT `async`. An `async` function suspends at its first
    // `await`, and everything after that runs in a later task — by which time
    // the browser no longer considers the click to be in progress and blocks
    // the pop-up. Safari enforces this strictly, and the failure is opaque:
    // FB.login never calls back at all, so the caller's spinner runs until the
    // watchdog fires, and the SDK reports POPUP_MAYBE_BLOCKED_OAUTH.
    //
    // When the SDK is already warm this returns the pop-up promise without
    // ever suspending, so FB.login runs inside the original gesture.
    if ((window as any).FB) {
      return this.runEmbeddedSignup((window as any).FB, setupData, options);
    }

    return this.loadFacebookSdk()
      .then(() => this.runEmbeddedSignup((window as any).FB, setupData, options));
  }

  /**
   * Run the signup pop-up. Assumes the SDK is loaded.
   *
   * @param FB Meta's loaded SDK.
   * @param setupData Optional prefill for the signup screens.
   * @param options Whether to offer the WhatsApp Business app branch.
   */
  private runEmbeddedSignup(
    FB: any,
    setupData?: WhatsAppSignupPrefill,
    options?: { coexistence?: boolean }
  ): Promise<{
    code: string;
    wabaId: string | null;
    phoneNumberId: string | null;
    businessId: string | null;
    coexistence: boolean;
  }> {
    return new Promise((resolve, reject) => {
      let sessionInfo: { waba_id?: string; phone_number_id?: string; business_id?: string } | null = null;
      let finishEvent: string | null = null;
      let signupError: SignupErrorDetail | null = null;
      let abandonedAtStep: string | null = null;

      const sessionInfoListener = (event: MessageEvent) => {
        if (!event.origin.endsWith('facebook.com')) return;
        try {
          const data = typeof event.data === 'string' ? JSON.parse(event.data) : event.data;
          if (data?.type !== 'WA_EMBEDDED_SIGNUP') return;

          if (WhatsAppAccountService.FINISH_EVENTS.has(data.event)) {
            // Every one of these is a completed signup, and treating only
            // FINISH as success reported the rest as "cancelled before it
            // completed" — a customer who had finished, told they had not.
            sessionInfo = data.data;
            finishEvent = data.event;
          } else if (data.event === 'CANCEL') {
            // Which screen they gave up on — worth knowing when signups stall.
            abandonedAtStep = data.data?.current_step ?? null;
          } else if (data.event === 'ERROR') {
            // session_id is what Meta support asks for first, so it is kept
            // rather than only logged.
            signupError = {
              step: data.data?.current_step ?? null,
              sessionId: data.data?.session_id ?? null,
              message: data.data?.error_message ?? null
            };
            console.error('WhatsApp signup reported an error', signupError);
          }
        } catch {
          // Not our message; facebook.com posts plenty of others.
        }
      };

      window.addEventListener('message', sessionInfoListener);

      // FB.login calls back when the popup completes or the user closes it —
      // but if the browser BLOCKS the popup outright it may never call back at
      // all, leaving the caller's spinner up forever. The old redirect flow
      // polled `popup.closed` for the same reason; this replaces that.
      let settled = false;
      const watchdog = window.setTimeout(() => {
        if (settled) return;
        settled = true;
        cleanup();
        reject(new Error(
          'The WhatsApp signup window did not open, or did not finish. ' +
          'If your browser blocked a pop-up, allow pop-ups for this site and try again.'
        ));
      }, WhatsAppAccountService.SIGNUP_TIMEOUT_MS);

      const cleanup = () => {
        window.clearTimeout(watchdog);
        window.removeEventListener('message', sessionInfoListener);
      };

      FB.login(
        (response: any) => {
          if (settled) return;
          settled = true;
          cleanup();
          const code = response?.authResponse?.code;
          if (!code) {
            // An error Meta reported is not a cancellation, and saying so sent
            // people looking for a mistake they had not made. The session id
            // goes on the error because it is the first thing Meta support
            // asks for.
            const failure = signupError ?
              new SignupFailedError(
                signupError.message ??
                  `WhatsApp signup failed${signupError.step ? ` at the "${signupError.step}" step` : ''}.`,
                signupError
              ) :
              new SignupFailedError(
                abandonedAtStep ?
                  `Signup was cancelled at the "${abandonedAtStep}" step.` :
                  'Signup was cancelled before it completed.',
                { step: abandonedAtStep, sessionId: null, message: null },
                true
              );
            reject(failure);
            return;
          }

          resolve({
            code,
            wabaId: sessionInfo?.waba_id ?? null,
            // Captured here, the backend no longer has to discover the phone
            // number by walking debug_token scopes with three fallbacks.
            phoneNumberId: sessionInfo?.phone_number_id ?? null,
            businessId: sessionInfo?.business_id ?? null,
            coexistence: finishEvent === 'FINISH_WHATSAPP_BUSINESS_APP_ONBOARDING'
          });
        },
        {
          config_id: environment.whatsapp.embeddedSignupConfigId,
          response_type: 'code',
          override_default_response_type: true,
          extras: {
            setup: setupData ?? {},
            // Offers the branch that adopts the number already running in the
            // customer's WhatsApp Business app. Omitted entirely otherwise —
            // sending it unconditionally would put the choice in front of
            // customers onboarding a fresh number, who have nothing to adopt.
            //
            // `featureType`, camelCase — confirmed against the extras Meta's
            // own Embedded Signup builder emits, which is worth more than the
            // docs: their v4 notes call it `feature_type` (that is the form
            // the Meta-hosted landing page takes as a query parameter, not
            // the form `extras` takes). Getting it wrong does not error. The
            // dialog just opens without the branch, which is indistinguishable
            // from the customer not having a Business app number.
            ...(options?.coexistence ?
              { featureType: 'whatsapp_business_app_onboarding' } :
              {})
          }
        }
      );
    });
  }

  /**
   * Get WhatsApp Business Profile for a phone number
   * @param phoneNumberId The phone number ID
   * @param organizationId The organization ID
   */
  getBusinessProfile(phoneNumberId: string, organizationId: string): Observable<WhatsAppBusinessProfile> {
    return from(this.getBusinessProfileFn({ phoneNumberId, organizationId })).pipe(
      map((result: any) => result.data as WhatsAppBusinessProfile)
    );
  }

  /**
   * Update WhatsApp Business Profile for a phone number
   * @param phoneNumberId The phone number ID
   * @param organizationId The organization ID
   * @param profileData The profile fields to update
   */
  updateBusinessProfile(phoneNumberId: string, organizationId: string, profileData: Partial<WhatsAppBusinessProfile>): Observable<any> {
    return from(this.updateBusinessProfileFn({ phoneNumberId, organizationId, ...profileData })).pipe(
      map((result: any) => result.data)
    );
  }

  /**
   * Update WhatsApp Business Profile picture
   * @param phoneNumberId The phone number ID
   * @param organizationId The organization ID
   * @param imageBase64 Base64-encoded image data
   * @param mimeType The MIME type of the image (e.g., image/jpeg)
   */
  updateProfilePicture(phoneNumberId: string, organizationId: string, imageBase64: string, mimeType: string): Observable<any> {
    return from(this.updateProfilePictureFn({ phoneNumberId, organizationId, imageBase64, mimeType })).pipe(
      map((result: any) => result.data)
    );
  }

  /**
   * Manually sync WhatsApp templates from Meta API to Firestore
   * Refreshes all templates for the organization
   */
  syncTemplates(): Observable<any> {
    return from(this.syncTemplatesFn({})).pipe(
      map((result: any) => result.data)
    );
  }
}
